import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ExternalLink, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DOC_STATUSES,
  HTTP_METHODS,
  PARAM_TYPES,
  extractPathParamNames,
  renderMarkdown,
  slugifyCategory,
  validateEndpointDoc,
  type DocStatus,
  type EndpointDoc,
  type Field,
  type HttpMethod,
  type Param,
  type ParamType,
} from "../../../../../../shared/index";

/**
 * The Endpoint editor — the docs document and, on the same screen, its `FeatureUrl`
 * row and Product links.
 *
 * They are one screen on purpose. The catalog half and the docs half were separate
 * systems in the legacy setup (SQL rows added by hand, docs edited in WordPress), and
 * keeping them apart is exactly what produced endpoints nobody documented and documents
 * pointing at permissions that did not exist. Here an editor cannot forget the other
 * half, because it is in front of them.
 *
 * Saving is still two writes — SQL through the write unit, Mongo for the document —
 * because they are different stores. The API runs the catalog half first, so a refused
 * write (readonly mode, a duplicate path, a rename blocked by live grants) leaves
 * nothing behind.
 */

type Mode = "create" | "edit";

interface CatalogFields {
  featureUrlId: number | null;
  featureUrlName: string;
  description: string;
  productIds: number[];
}

const EMPTY_DOC: Omit<EndpointDoc, "updatedAt" | "updatedBy" | "version"> = {
  _id: "",
  kind: "endpoint",
  featureUrlId: null,
  method: "GET",
  path: "",
  title: "",
  summary: "",
  body: "",
  category: { id: "", name: "", order: 99 },
  products: [],
  pathParams: [],
  queryParams: [],
  response: { fields: [] },
  status: "draft",
  visibleOnDocs: true,
};

export default function B2bEndpointEditor() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const mode: Mode = slug ? "edit" : "create";

  const existing = useQuery({
    queryKey: ["b2b", "catalog", "doc", slug],
    queryFn: () => api.b2b.endpointDoc(slug!),
    enabled: mode === "edit",
  });
  const lookups = useQuery({ queryKey: ["b2b", "lookups"], queryFn: api.b2b.lookups });
  const view = useQuery({ queryKey: ["b2b", "catalog", "view"], queryFn: api.b2b.catalogView });

  // `?path=` arrives from the catalog page's "Write docs" action on an endpoint that
  // has a FeatureUrl but no document. It has to seed BOTH halves: the catalog field and
  // the document's own `path`, which is what the docs site renders and what the path
  // params are derived from. Seeding only the catalog field left the form permanently
  // invalid with "path: is not an absolute path" and no obvious cause.
  const prefilledPath = params.get("path") ?? "";
  const [doc, setDoc] = useState<Omit<EndpointDoc, "updatedAt" | "updatedBy" | "version">>({
    ...EMPTY_DOC,
    path: prefilledPath,
  });
  // Present when documenting an endpoint that is already in the catalog: its row is
  // edited rather than created, so "Write docs" does not try to re-create it.
  const prefilledFeatureUrlId = params.get("featureUrlId");
  const [catalog, setCatalog] = useState<CatalogFields>({
    featureUrlId: prefilledFeatureUrlId ? Number(prefilledFeatureUrlId) : null,
    featureUrlName: prefilledPath,
    description: "",
    productIds: [],
  });
  const [loadedVersion, setLoadedVersion] = useState<number | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Seed the form once the document arrives. Keyed on the loaded version so a save
  // (which bumps it) refreshes the form from the server's copy rather than leaving the
  // editor holding a document that no longer matches what is stored.
  useEffect(() => {
    if (!existing.data) return;
    const { updatedAt: _a, updatedBy: _b, version, ...rest } = existing.data;
    setDoc(rest);
    setLoadedVersion(version);
  }, [existing.data]);

  /**
   * Seed the catalog half from the row that already exists.
   *
   * This runs for an edit **and** for "Write docs" on an already-catalogued endpoint,
   * and it is not cosmetic: the save sends the complete desired set of Products, so a
   * form that never loaded them would submit an empty list and silently strip the
   * endpoint out of every Product it belonged to. That is exactly what happened the
   * first time this flow ran end to end — the change log read "-1 product".
   */
  const linkedFeatureUrlId = existing.data?.featureUrlId ?? catalog.featureUrlId;
  useEffect(() => {
    if (!view.data || linkedFeatureUrlId === null) return;
    const row = view.data.rows.find((r) => r.featureUrlId === linkedFeatureUrlId);
    if (!row) return;
    setCatalog((current) => ({
      ...current,
      featureUrlId: row.featureUrlId,
      featureUrlName: row.path,
      description: row.description ?? "",
      productIds: (lookups.data?.products ?? [])
        .filter((p) => row.products.includes(p.productName))
        .map((p) => p.productId),
    }));
    // Only on the first load of this row: afterwards the operator's edits own the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.data, lookups.data, linkedFeatureUrlId]);

  /**
   * Path params follow the path. The legacy docs shipped `{ticker}` placeholders with
   * nothing describing them, so a reader could not tell what to put there — the braces
   * in the path are the source of truth, and a param is added or dropped to match while
   * keeping any description already written.
   */
  const pathParamNames = useMemo(() => extractPathParamNames(doc.path), [doc.path]);
  useEffect(() => {
    setDoc((current) => {
      const byName = new Map(current.pathParams.map((p) => [p.name, p]));
      const next = pathParamNames.map(
        (name) =>
          byName.get(name) ?? {
            name,
            type: "string" as ParamType,
            required: true,
            description: "",
          }
      );
      const unchanged =
        next.length === current.pathParams.length &&
        next.every((p, i) => p === current.pathParams[i]);
      return unchanged ? current : { ...current, pathParams: next };
    });
  }, [pathParamNames]);

  const problems = useMemo(
    () => validateEndpointDoc({ ...doc, updatedAt: new Date(), updatedBy: "", version: 1 }),
    [doc]
  );

  const save = useMutation({
    mutationFn: async () => {
      if (mode === "create") {
        return api.b2b.createEndpoint({
          featureUrlName: catalog.featureUrlName,
          description: catalog.description || null,
          productIds: catalog.productIds,
          featureUrlId: catalog.featureUrlId ?? undefined,
          doc: { ...doc, _id: doc._id || slugifyCategory(doc.title) },
        });
      }
      return api.b2b.saveEndpoint(slug!, {
        doc,
        expectedVersion: loadedVersion ?? undefined,
        catalog: catalog.featureUrlId
          ? {
              featureUrlId: catalog.featureUrlId,
              featureUrlName: catalog.featureUrlName,
              description: catalog.description || null,
              productIds: catalog.productIds,
            }
          : undefined,
      });
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["b2b", "catalog"] });
      const applied = "applied" in result ? result.applied : true;
      toast.success(
        applied ? "Saved." : "Dry run — the catalog change was recorded but rolled back."
      );
      if (mode === "create") navigate("/dashboard/b2b/catalog");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const preview = useMutation({
    mutationFn: () => api.b2b.previewToken(slug!),
    onSuccess: (result) => {
      navigator.clipboard?.writeText(result.url);
      toast.success(`Preview link copied — expires ${new Date(result.expiresAt).toLocaleString()}`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const products = lookups.data?.products ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">
            {mode === "create" ? "New endpoint" : doc.title || slug}
          </h1>
          <p className="text-sm text-muted-foreground">
            {mode === "create"
              ? "Creates the FeatureUrl row, its Product links and the document"
              : `Document version ${loadedVersion ?? "…"}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => navigate("/dashboard/b2b/catalog")}>
            Cancel
          </Button>
          {mode === "edit" && (
            <Button
              variant="secondary"
              onClick={() => preview.mutate()}
              disabled={preview.isPending}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Copy draft preview link
            </Button>
          )}
          <Button onClick={() => save.mutate()} disabled={save.isPending || problems.length > 0}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {problems.length > 0 && (
        <div className="space-y-1 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
          <div className="flex items-center gap-2 font-medium">
            <AlertTriangle className="h-4 w-4" />
            Fix before saving
          </div>
          <ul className="ml-6 list-disc">
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      )}

      <Section title="Catalog — what a customer can be granted">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field2 label="Endpoint path" hint="Absolute, no query string. {braces} are allowed.">
            <Input
              value={catalog.featureUrlName}
              onChange={(e) => {
                setCatalog({ ...catalog, featureUrlName: e.target.value });
                setDoc((d) => ({ ...d, path: e.target.value }));
              }}
              placeholder="/api/stocks/marketMovers"
            />
          </Field2>
          <Field2 label="Catalog description" hint="Shown in the access matrix, not on the docs site.">
            <Input
              value={catalog.description}
              onChange={(e) => setCatalog({ ...catalog, description: e.target.value })}
            />
          </Field2>
        </div>

        <Field2 label="Products" hint="Which sales bundles include this endpoint.">
          <div className="flex flex-wrap gap-2">
            {products.map((product) => {
              const on = catalog.productIds.includes(product.productId);
              return (
                <button
                  key={product.productId}
                  type="button"
                  onClick={() =>
                    setCatalog({
                      ...catalog,
                      productIds: on
                        ? catalog.productIds.filter((id) => id !== product.productId)
                        : [...catalog.productIds, product.productId],
                    })
                  }
                  className={`rounded-full border px-3 py-1 text-xs ${
                    on ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted"
                  }`}
                >
                  {product.productName}
                </button>
              );
            })}
            {products.length === 0 && (
              <span className="text-xs text-muted-foreground">No products in the catalog.</span>
            )}
          </div>
        </Field2>

        {mode === "edit" && catalog.featureUrlId !== null && (
          <p className="text-xs text-muted-foreground">
            Renaming the path is refused while any grant still references it — the runtimes
            authorise on that stored string, so the grants must be moved first.
          </p>
        )}
      </Section>

      <Section title="Document — what a reader sees">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field2 label="Slug" hint="URL segment on the docs site. Stable once published.">
            <Input
              value={doc._id}
              disabled={mode === "edit"}
              onChange={(e) => setDoc({ ...doc, _id: e.target.value })}
              placeholder="market-movers"
            />
          </Field2>
          <Field2 label="Title">
            <Input value={doc.title} onChange={(e) => setDoc({ ...doc, title: e.target.value })} />
          </Field2>
          <Field2 label="Method">
            <select
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              value={doc.method}
              onChange={(e) => setDoc({ ...doc, method: e.target.value as HttpMethod })}
            >
              {HTTP_METHODS.map((method) => (
                <option key={method}>{method}</option>
              ))}
            </select>
          </Field2>
          <Field2 label="Status">
            <select
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              value={doc.status}
              onChange={(e) => setDoc({ ...doc, status: e.target.value as DocStatus })}
            >
              {DOC_STATUSES.map((status) => (
                <option key={status}>{status}</option>
              ))}
            </select>
          </Field2>
          <Field2 label="Category">
            <Input
              value={doc.category.name}
              onChange={(e) =>
                setDoc({
                  ...doc,
                  category: {
                    ...doc.category,
                    name: e.target.value,
                    id: slugifyCategory(e.target.value),
                  },
                })
              }
            />
          </Field2>
          <Field2 label="Visible on the docs site">
            <label className="flex h-9 items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={doc.visibleOnDocs}
                onChange={(e) => setDoc({ ...doc, visibleOnDocs: e.target.checked })}
              />
              Listed in the sidebar and the sitemap
            </label>
          </Field2>
        </div>

        <Field2 label="Summary" hint="One line — the sidebar, the list and the SEO description.">
          <Input value={doc.summary} onChange={(e) => setDoc({ ...doc, summary: e.target.value })} />
        </Field2>

        <Field2 label="Body (Markdown)">
          <div className="flex items-center gap-2 pb-1">
            <Button
              type="button"
              size="sm"
              variant={showPreview ? "ghost" : "secondary"}
              onClick={() => setShowPreview(false)}
            >
              Write
            </Button>
            <Button
              type="button"
              size="sm"
              variant={showPreview ? "secondary" : "ghost"}
              onClick={() => setShowPreview(true)}
            >
              Preview
            </Button>
          </div>
          {showPreview ? (
            <div
              className="prose prose-sm min-h-[12rem] max-w-none rounded-md border p-3 dark:prose-invert"
              // Safe by construction: `renderMarkdown` escapes the whole input before
              // emitting any markup, so nothing the author types can become a tag.
              dangerouslySetInnerHTML={{ __html: renderMarkdown(doc.body) }}
            />
          ) : (
            <textarea
              className="min-h-[12rem] w-full rounded-md border bg-background p-3 font-mono text-sm"
              value={doc.body}
              onChange={(e) => setDoc({ ...doc, body: e.target.value })}
            />
          )}
        </Field2>
      </Section>

      <Section title="Parameters">
        <div className="space-y-1">
          <Label>Path parameters</Label>
          <p className="text-xs text-muted-foreground">
            Derived from the {"{braces}"} in the path — add or remove one by editing the path.
          </p>
          {doc.pathParams.length === 0 ? (
            <p className="text-sm text-muted-foreground">None.</p>
          ) : (
            <ParamEditor
              params={doc.pathParams}
              onChange={(pathParams) => setDoc({ ...doc, pathParams })}
              lockNames
            />
          )}
        </div>

        <div className="space-y-1">
          <Label>Query parameters</Label>
          <ParamEditor
            params={doc.queryParams}
            onChange={(queryParams) => setDoc({ ...doc, queryParams })}
          />
        </div>
      </Section>

      <Section title="Response fields">
        <FieldEditor
          fields={doc.response.fields}
          onChange={(fields) => setDoc({ ...doc, response: { ...doc.response, fields } })}
        />
      </Section>
    </div>
  );
}

// ── Building blocks ─────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4 rounded-lg border p-4">
      <h2 className="font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function Field2({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function ParamEditor({
  params,
  onChange,
  lockNames = false,
}: {
  params: Param[];
  onChange: (params: Param[]) => void;
  lockNames?: boolean;
}) {
  const update = (index: number, patch: Partial<Param>) =>
    onChange(params.map((p, i) => (i === index ? { ...p, ...patch } : p)));

  return (
    <div className="space-y-2">
      {params.map((param, index) => (
        <div key={index} className="grid gap-2 sm:grid-cols-[1fr_8rem_6rem_2fr_2rem]">
          <Input
            value={param.name}
            disabled={lockNames}
            placeholder="name"
            onChange={(e) => update(index, { name: e.target.value })}
          />
          <select
            className="h-9 rounded-md border bg-background px-2 text-sm"
            value={param.type}
            onChange={(e) => update(index, { type: e.target.value as ParamType })}
          >
            {PARAM_TYPES.map((type) => (
              <option key={type}>{type}</option>
            ))}
          </select>
          <label className="flex h-9 items-center gap-1 text-xs">
            <input
              type="checkbox"
              checked={param.required}
              onChange={(e) => update(index, { required: e.target.checked })}
            />
            required
          </label>
          <Input
            value={param.description}
            placeholder="description"
            onChange={(e) => update(index, { description: e.target.value })}
          />
          {!lockNames && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange(params.filter((_, i) => i !== index))}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          {param.type === "enum" && (
            <div className="sm:col-span-5">
              <Input
                value={(param.enum ?? []).join(", ")}
                placeholder="Options, comma separated — e.g. 1 - DollarVolume, 2 - PercentGainers"
                onChange={(e) =>
                  update(index, {
                    enum: e.target.value
                      .split(",")
                      .map((option) => option.trim())
                      .filter(Boolean),
                  })
                }
              />
            </div>
          )}
        </div>
      ))}
      {!lockNames && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            onChange([...params, { name: "", type: "string", required: false, description: "" }])
          }
        >
          <Plus className="mr-2 h-4 w-4" />
          Add parameter
        </Button>
      )}
    </div>
  );
}

function FieldEditor({
  fields,
  onChange,
}: {
  fields: Field[];
  onChange: (fields: Field[]) => void;
}) {
  const update = (index: number, patch: Partial<Field>) =>
    onChange(fields.map((f, i) => (i === index ? { ...f, ...patch } : f)));

  return (
    <div className="space-y-2">
      {fields.map((field, index) => (
        <div key={index} className="grid gap-2 sm:grid-cols-[1fr_8rem_2fr_2rem]">
          <Input
            value={field.name}
            placeholder="name"
            onChange={(e) => update(index, { name: e.target.value })}
          />
          <Input
            value={field.type}
            placeholder="type"
            onChange={(e) => update(index, { type: e.target.value })}
          />
          <Input
            value={field.description}
            placeholder="description"
            onChange={(e) => update(index, { description: e.target.value })}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange(fields.filter((_, i) => i !== index))}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          {field.fields && field.fields.length > 0 && (
            <div className="sm:col-span-4 sm:pl-6">
              <Badge variant="outline" className="mb-1">
                {field.fields.length} nested field{field.fields.length === 1 ? "" : "s"}
              </Badge>
              <FieldEditor
                fields={field.fields}
                onChange={(nested) => update(index, { fields: nested })}
              />
            </div>
          )}
        </div>
      ))}
      <Button
        variant="secondary"
        size="sm"
        onClick={() => onChange([...fields, { name: "", type: "string", description: "" }])}
      >
        <Plus className="mr-2 h-4 w-4" />
        Add field
      </Button>
    </div>
  );
}
